import React from 'react';
import { useDocumentFieldItem } from '../../../hooks/useDocumentFieldItem';
import styles from '../../../styles/DocumentManagement.module.css';
import { DocumentFieldsItemProps } from '../../../types/DocumentManagement';

// Props interface now imported from types file, with additional props
interface ExtendedDocumentFieldsItemProps extends DocumentFieldsItemProps {
  loading?: boolean;
  isCompanyMode?: boolean;
}

const DocumentFieldItem: React.FC<ExtendedDocumentFieldsItemProps> = ({ 
  field, 
  value, 
  onChange, 
  loading,
  isCompanyMode = false
}) => {
  const [inputEnabled, setInputEnabled] = React.useState(true);
  const {
    // State
    isHighlighted,
    isFading,
    processing,
    reminderJustSent,
    reminderSentToday,
    containerRef,
    
    // Context data
    loading: ctxLoading,
    
    // Handlers
    handleSendReminder,
    
    // Permission/UI flags
    canEdit,
    showReminderIndicator,
    showSendReminderButton,
    showReminderSentIndicator,
    
    // Computed values
    formattedValue,
  } = useDocumentFieldItem({ field, value, isCompanyMode });

  const handleCheck = () => {
    setInputEnabled(!inputEnabled);
  }

  const isLoading = loading ?? ctxLoading;

  return (
    <div
      ref={containerRef}
      className={
        isHighlighted
          ? `${styles.fieldTypeItemCard} ${styles.field_type_item_highlighted} ${isFading ? styles.field_type_item_fading : ''}`
          : styles.fieldTypeItemCard
      }
    >
      <div className={styles.fieldTypeItemHeader}>
        <label className={styles.fieldTypeItemLabel}>
          <span className={styles.document_type_item_title}>✍️ {field.name}</span>
        </label>


        {!field.required && <label className={styles.fieldTypeItemLabel}>
          <span className={styles.document_type_item_title}>Applicable</span>
          <input
            type="checkbox"
            className={styles.checkboxLg}
            onChange={() => handleCheck()}
            checked={inputEnabled}
          />
        </label>}
        
        {/* Reminder Indicator - Agent viewing their own fields */}
        {showReminderIndicator && (
          <span
            className={styles.reminderIndicator}
            title="You have a reminder for this field"
            aria-label="You have a reminder for this field"
          >
            <span role="img" aria-label="Reminder">🔔</span>
          </span>
        )}
        
        {/* Send Reminder Button - Manager viewing others, no reminder exists */}
        {showSendReminderButton && (
          <button
            type="button"
            className={styles.reminderButton}
            onClick={() => handleSendReminder(field.id)}
            disabled={processing || reminderJustSent || reminderSentToday}
            aria-label={reminderSentToday ? 'Reminder already sent today' : `Send reminder to agent to complete field: ${field.name}`}
            title={reminderSentToday ? 'Reminder already sent today' : `Send reminder to agent to complete field: ${field.name}`}
          >
            <span role="img" aria-label="Send Reminder">🔔</span>
          </button>
        )}
        
        {/* Reminder Sent Indicator - Manager viewing others, reminder exists */}
        {showReminderSentIndicator && (
          <div className={styles.reminderSentIndicator}>
            <span role="img" aria-label="Reminder Sent">✓</span> Reminder Sent
          </div>
        )}
      </div>
      {field.fieldType === 'date' ? (
        <input
          className={styles.fieldTypeItemInput}
          type="date"
          value={formattedValue}
          onChange={e => onChange(e.target.value)}
          disabled={isLoading || !canEdit || !inputEnabled}
        />
      ) : field.fieldType === 'number' ? (
        <input
          className={styles.fieldTypeItemInput}
          type="number"
          value={formattedValue}
          onChange={e => onChange(e.target.value)}
          disabled={isLoading || !canEdit || !inputEnabled}
        />
      ) : field.fieldType === 'boolean' ? (
        <select
          onChange={e => {
            const val = e.target.value;
            onChange(e.target.value);
          }}
          value={formattedValue}
          disabled={isLoading || !canEdit || !inputEnabled}
          className={styles.selectboxLg}
        >
          {formattedValue === '' && (
            <option value="" disabled>
              Select
            </option>
          )}
          <option value="false">False</option>
          <option value="true">True</option>
        </select>
      ) : (
        <input
          className={styles.fieldTypeItemInput}
          type="text"
          value={formattedValue}
          onChange={e => onChange(e.target.value)}
          disabled={isLoading || !canEdit || !inputEnabled}
        />
      )}
    </div>
  );
};

export default DocumentFieldItem;
